import React from 'react';
import { motion } from 'framer-motion';
import { renderCell } from '../utils/helpers';
import {
  getTableLayoutFromColumns,
  getTableLayoutFromWeights,
  isActionsColumnMeta,
  resolveTableContainerStyle,
} from '../utils/tableColumnWidths';

const isActionsColumn = (column) => isActionsColumnMeta(column);

const cellPad = 'px-2 sm:px-3 py-2 sm:py-2.5';

export const TABLE_HEADER_CELL_CLASS =
  'align-middle whitespace-nowrap text-left';

export const TABLE_ACTIONS_HEADER_CLASS =
  'align-middle whitespace-nowrap text-center';

export const TABLE_ACTIONS_CELL_CLASS = 'text-center';

/**
 * @param {object} props
 * @param {number} [props.columnCount] - legacy: equal split if no weights
 * @param {Array} [props.columns] - column defs (uses smart weights)
 * @param {number[]} [props.weights] - explicit weight per column (inventaire grids)
 * @param {string[]} [props.headers] - header labels (required with weights for title sizing)
 */
export function resolveTableLayout({ columnCount, columns = null, weights = null, headers = null }) {
  if (columns?.length) return getTableLayoutFromColumns(columns);
  if (weights?.length) return getTableLayoutFromWeights(weights, headers);
  if (columnCount) return getTableLayoutFromWeights(Array(columnCount).fill(1));
  return { widths: [], minWidthsCh: [], tableMinWidthCh: 0 };
}

export function TableColGroup({ columnCount, columns = null, weights = null, headers = null }) {
  const layout = resolveTableLayout({ columnCount, columns, weights, headers });
  const { widths, minWidthsCh } = layout;

  if (!widths.length) return null;

  return (
    <colgroup>
      {widths.map((width, index) => {
        const colDef = columns?.[index];
        const useExplicitPct = typeof colDef?.width === 'string' && colDef.width.includes('%');
        return (
          <col
            key={index}
            style={{
              width,
              minWidth:
                !useExplicitPct && minWidthsCh?.[index]
                  ? `${minWidthsCh[index]}ch`
                  : undefined,
            }}
          />
        );
      })}
    </colgroup>
  );
}

const Table = ({
  columns,
  data,
  onRowClick = null,
  emptyMessage = 'Aucune donnée disponible',
  layout = 'auto',
}) => {
  const { tableMinWidthCh } = resolveTableLayout({ columns });
  const hasExplicitColWidths = columns?.length > 0 && columns.every((c) => c.width);
  const { style: tableStyle, compact: compactTable } = hasExplicitColWidths
    ? { style: { width: '100%' }, compact: false }
    : resolveTableContainerStyle(tableMinWidthCh, layout);

  return (
    <div className="w-full min-w-0 overflow-x-auto -mx-3 sm:mx-0 pfe-table-scroll shadow-sm ring-1 ring-black ring-opacity-5 sm:rounded-lg">
      <table
        style={tableStyle}
        className={`pfe-data-table table-fixed divide-y divide-gray-200 ${
          compactTable ? 'max-w-full' : 'w-full min-w-full'
        }`}
      >
        <TableColGroup columns={columns} />
        <thead className="bg-gray-50">
          <tr>
            {columns.map((column, index) => (
              <th
                key={index}
                scope="col"
                className={`${cellPad} ${
                  isActionsColumn(column) ? '' : 'overflow-hidden'
                } ${
                  isActionsColumn(column) ? TABLE_ACTIONS_HEADER_CLASS : TABLE_HEADER_CELL_CLASS
                } text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-normal ${
                  column.headerClassName || ''
                }`}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {data.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="px-3 py-6 sm:py-8 text-center text-gray-500 text-sm">
                {emptyMessage}
              </td>
            </tr>
          ) : (
            data.map((row, rowIndex) => (
              <motion.tr
                key={rowIndex}
                initial={{ opacity: 0.95 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.1 }}
                onClick={() => onRowClick && onRowClick(row)}
                className={`group ${onRowClick ? 'hover:bg-gray-50 cursor-pointer' : 'hover:bg-gray-50'}`}
              >
                {columns.map((column, colIndex) => (
                  <td
                    key={colIndex}
                    className={`${cellPad} text-[11px] sm:text-sm text-gray-900 align-middle overflow-hidden ${
                      isActionsColumn(column) ? TABLE_ACTIONS_CELL_CLASS : ''
                    } ${column.cellClassName || ''}`}
                  >
                    {column.render ? (
                      isActionsColumn(column) ? (
                        <div className="mx-auto max-w-full flex justify-center flex-wrap gap-0.5">
                          {column.render(row)}
                        </div>
                      ) : (
                        column.render(row)
                      )
                    ) : (
                      <div
                        className={
                          column.nowrap === false
                            ? 'break-words min-w-0'
                            : 'truncate min-w-0'
                        }
                        title={
                          typeof row[column.accessor] === 'string'
                            ? row[column.accessor]
                            : undefined
                        }
                      >
                        {renderCell(row[column.accessor])}
                      </div>
                    )}
                  </td>
                ))}
              </motion.tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

export default Table;
